"use client"

import { useEffect, useState } from "react"
import { getCTAs, createCTA, updateCTA, deleteCTA } from "../utils/api"
import { FaPlus, FaTrash, FaEdit } from "react-icons/fa"

const CallToAction = () => {
  const [ctas, setCtas] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ role: "", number: "" })
  const [editingId, setEditingId] = useState(null)

  // Fetch all CTAs
  const fetchCTAs = async () => {
    setLoading(true)
    try {
      const data = await getCTAs()
      setCtas(data)
    } catch (err) {
      console.error("Error fetching CTAs:", err)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchCTAs()
  }, [])

  // Handle form submit (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editingId) {
        await updateCTA(editingId, form)
      } else {
        await createCTA(form)
      }
      setForm({ role: "", number: "" })
      setEditingId(null)
      fetchCTAs()
    } catch (err) {
      console.error("Error saving CTA:", err)
    }
    setSaving(false)
  }

  // Handle edit
  const handleEdit = (cta) => {
    setForm({ role: cta.role, number: cta.number })
    setEditingId(cta._id)
  }

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Call-to-Action?")) return
    try {
      await deleteCTA(id)
      fetchCTAs()
    } catch (err) {
      console.error("Error deleting CTA:", err)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-2">
            Call-to-Action Manager
          </h1>
          <p className="text-slate-600">Manage your call-to-actions with style</p>
        </div>

        {/* Form */}
        <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-3xl shadow-2xl shadow-blue-500/10 p-8 mb-8 hover:shadow-blue-500/20 transition-all duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <FaPlus className="text-white text-sm" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">
              {editingId ? "Edit Call to Action" : "Add Call to Action"}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Role</label>
                <input
                  type="text"
                  placeholder="Enter role..."
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 placeholder-slate-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Number</label>
                <input
                  type="text"
                  placeholder="Enter number..."
                  value={form.number}
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                  className="w-full bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 placeholder-slate-400"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-8 py-4 rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 group"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  {editingId ? "Update CTA" : "Add CTA"}
                  <FaPlus className="group-hover:rotate-90 transition-transform duration-300" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* List */}
        <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-3xl shadow-2xl shadow-slate-500/10 p-8 hover:shadow-slate-500/20 transition-all duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-slate-600 to-slate-800 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm">{ctas.length}</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-800">All Call-to-Actions</h2>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-pulse bg-gradient-to-r from-slate-100 to-slate-50 rounded-2xl p-6 border border-slate-200/50"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1 space-y-3">
                      <div className="h-5 bg-gradient-to-r from-slate-300 to-slate-200 rounded-xl w-32"></div>
                      <div className="h-4 bg-gradient-to-r from-slate-200 to-slate-100 rounded-lg w-24"></div>
                    </div>
                    <div className="flex gap-3">
                      <div className="h-10 w-10 bg-slate-200 rounded-xl"></div>
                      <div className="h-10 w-10 bg-slate-200 rounded-xl"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : ctas.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gradient-to-r from-slate-200 to-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaPlus className="text-slate-500 text-2xl" />
              </div>
              <p className="text-slate-500 text-lg font-medium">No call-to-actions found</p>
              <p className="text-slate-400 text-sm mt-1">Create your first CTA to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ctas.map((cta, index) => (
                <div
                  key={cta._id}
                  className="group bg-gradient-to-r from-white/80 to-white/60 backdrop-blur-sm border border-white/30 rounded-2xl p-6 hover:shadow-lg hover:shadow-slate-500/10 transition-all duration-300 hover:scale-[1.02]"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"></div>
                        <p className="font-bold text-slate-800 text-lg">{cta.role}</p>
                      </div>
                      <p className="text-slate-600 font-medium ml-6">{cta.number}</p>
                    </div>

                    <div className="flex gap-2 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => handleEdit(cta)}
                        className="w-10 h-10 bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 hover:text-blue-700 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
                      >
                        <FaEdit className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleDelete(cta._id)}
                        className="w-10 h-10 bg-red-500/10 hover:bg-red-500/20 text-red-600 hover:text-red-700 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CallToAction;